<div class="global-search">
  <!-- Input row -->
  <div class="search-input-row">
    <mat-icon class="search-icon" [svgIcon]="ICON.magnifyingGlass" aria-hidden="true" />

    <input
      #combobox="ngCombobox"
      ngCombobox
      filterMode="manual"
      class="search-input"
      [preserveContent]="true"
      [placeholder]="dialog.context?.placeholder ?? 'Suchen…'"
      [(value)]="search.query"
      [(expanded)]="popupExpanded"
    />

    <kbd class="key-hint" aria-hidden="true">Esc</kbd>
  </div>

  <!-- Scope + type-filter chips -->
  @if (scopeChips().length > 0 || search.connectorFilter() !== null) {
    <div class="scope-row">
      @for (chip of scopeChips(); track chip.key) {
        <span class="scope-chip">
          {{ chip.label }}
          <button
            immoIconButton
            class="scope-chip-remove"
            size="xs"
            [icon]="ICON.xSimple"
            [attr.aria-label]="'Filter entfernen: ' + chip.label"
            (mousedown)="$event.preventDefault()"
            (clicked)="onRemoveScope(chip.key)"
          ></button>
        </span>
      }

      @if (search.connectorFilter(); as connectorFilter) {
        <span class="scope-chip">
          Nur: {{ connectorFilter.label }}
          <button
            immoIconButton
            class="scope-chip-remove"
            size="xs"
            [icon]="ICON.xSimple"
            [attr.aria-label]="'Filter entfernen: Nur ' + connectorFilter.label"
            (mousedown)="$event.preventDefault()"
            (clicked)="onRemoveConnectorFilter()"
          ></button>
        </span>
      }
    </div>
  }

  <!-- Screen reader result announcements -->
  <div aria-live="polite" class="visually-hidden">{{ liveMessage() }}</div>

  <!-- Result panel: ngComboboxPopup stamps its content right here, inside the dialog.
       The widget is an ARIA grid in activedescendant mode: focus stays in the input,
       ↑/↓ move between result rows, ←/→ move between a row's cells (main + actions),
       Enter activates the active cell. GridCell marks the active cell with the
       `data-active` attribute (source-verified) — that drives the visual highlight. -->
  <ng-template ngComboboxPopup popupType="grid" [combobox]="combobox">
    <div class="results-panel">
      <div
        #grid="ngGrid"
        ngGrid
        ngComboboxWidget
        class="results-grid"
        focusMode="activedescendant"
        colWrap="nowrap"
        [enableSelection]="false"
        [softDisabled]="false"
        [tabindex]="-1"
        [activeDescendant]="grid.activeDescendant()"
        (keydown.enter)="onCommit(grid.activeDescendant())"
        (keydown.control.enter)="onSecondaryCommit($event, grid.activeDescendant())"
        (keydown.meta.enter)="onSecondaryCommit($event, grid.activeDescendant())"
      >
        <!-- Recent searches — shown instead of results while the input is empty.
             mousedown.preventDefault keeps the combobox focus in the input. -->
        @if (showRecentSearches()) {
          <div class="section-header">
            <span class="section-label">Letzte Suchen</span>
          </div>

          @for (recent of search.recentSearches(); track recent) {
            <button
              type="button"
              class="recent-search-row"
              (mousedown)="$event.preventDefault()"
              (click)="onRecentSearchClick(recent)"
            >
              <mat-icon [svgIcon]="ICON.clock" aria-hidden="true" />
              <span class="result-title">{{ recent }}</span>
            </button>
          }
        }

        @for (section of sectionViews(); track section.connector.id) {
          <div role="rowgroup" [attr.aria-labelledby]="'gs-section-' + section.connector.id">
            <div class="section-header" [id]="'gs-section-' + section.connector.id">
              <span class="section-label">{{ section.connector.label }}</span>

              @if (section.status === 'loading') {
                <span class="section-loading" aria-hidden="true"></span>
              } @else if (section.status === 'error') {
                <span class="section-error">Fehler beim Laden</span>
              } @else {
                <span class="section-count">{{ section.entries.length }}</span>
              }

              <!-- Type filter: restrict the palette to this section. Hidden while
                   already filtered (the chip above is the way back). -->
              @if (search.connectorFilter() === null) {
                <button
                  type="button"
                  class="section-filter"
                  (mousedown)="$event.preventDefault()"
                  (click)="onFilterConnector(section.connector.id, section.connector.label)"
                >
                  Nur diese
                </button>
              }
            </div>

            @for (entry of section.entries; track $index) {
              <div ngGridRow class="result-row">
                <!-- Main cell: the result itself. Enter / click runs the primary action. -->
                <div
                  ngGridCell
                  class="result-cell result-cell-main"
                  [id]="entry.key"
                  (click)="onEntryClick(entry)"
                >
                  <span class="result-avatar" aria-hidden="true">
                    <mat-icon [svgIcon]="entry.item.icon ?? section.connector.icon ?? ICON.file" />
                  </span>

                  <span class="result-text">
                    <span class="result-title">{{ entry.item.title }}</span>

                    @if (!IsNullOrUndefinedOrEmpty(entry.item.subtitle)) {
                      <span class="result-subtitle">{{ entry.item.subtitle }}</span>
                    }

                    @if (!IsNullOrUndefinedOrEmpty(entry.item.badge)) {
                      <span class="result-badge">{{ entry.item.badge }}</span>
                    }
                  </span>

                  <mat-icon class="result-chevron" [svgIcon]="ICON.caretRight" aria-hidden="true" />
                </div>

                <!-- Secondary actions as real grid cells: reachable with →, activated with
                     Enter or click. Revealed when the row is hovered or holds the active cell. -->
                @for (action of entry.item.actions.slice(1); track action.id) {
                  <div
                    ngGridCell
                    matRipple
                    class="result-cell result-cell-action"
                    [id]="entry.key + '::' + action.id"
                    [attr.aria-label]="action.label"
                    [title]="action.label"
                    (click)="onActionCellClick($event, entry, action)"
                  >
                    <mat-icon [svgIcon]="action.icon ?? ICON.dotsThreeOutline" aria-hidden="true" />
                  </div>
                }
              </div>
            }
          </div>
        }

        @if (isEmpty()) {
          <div class="empty-state">Keine Ergebnisse für „{{ search.query() }}“</div>
        }
      </div>
    </div>
  </ng-template>

  <!-- FOOTER -->
  <div class="footer-hints" aria-hidden="true">
    <div><kbd class="key-hint">↑</kbd><kbd class="key-hint">↓</kbd> Navigieren</div>
    <div><kbd class="key-hint">←</kbd><kbd class="key-hint">→</kbd> Aktionen</div>
    <div><kbd class="key-hint">↵</kbd> Ausführen</div>
    <div><kbd class="key-hint">Esc</kbd> Schließen</div>
  </div>
  <!-- footer -->
</div>
